import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { OpaqueCardConfig, SolidColorScheme, CardVariant, CardSize } from '@/types/opaqueCards';

interface OpaqueCardsSliceState {
  /** Global configuration for opaque cards */
  config: OpaqueCardConfig;
  
  /** Current card styling variant */
  cardVariant: CardVariant;
  
  /** Current card size */
  cardSize: CardSize;
  
  /** Custom color schemes */
  colorSchemes: {
    card: SolidColorScheme;
    saveIndicator: SolidColorScheme;
    passIndicator: SolidColorScheme;
  };
  
  /** Whether opaque mode is enabled */
  isEnabled: boolean;
  
  /** Performance settings */
  performance: {
    enableTransitions: boolean;
    enableShadows: boolean;
    enableBorders: boolean;
  };
}

const initialState: OpaqueCardsSliceState = {
  config: {
    disableOpacity: true,
    forceSolidBackgrounds: true,
    enhanceContrast: true,
    useSolidShadows: true,
    useThemeColors: true,
  },
  cardVariant: 'default',
  cardSize: 'md',
  colorSchemes: {
    card: {
      background: 'bg-card',
      foreground: 'text-card-foreground',
      border: 'border-border',
    },
    saveIndicator: {
      background: 'bg-emerald-600',
      foreground: 'text-white',
      border: 'border-emerald-700',
      hover: 'hover:bg-emerald-700',
    },
    passIndicator: {
      background: 'bg-red-600',
      foreground: 'text-white',
      border: 'border-red-700',
      hover: 'hover:bg-red-700',
    },
  },
  isEnabled: true,
  performance: {
    enableTransitions: true,
    enableShadows: true,
    enableBorders: true,
  },
};

const opaqueCardsSlice = createSlice({
  name: 'opaqueCards',
  initialState,
  reducers: {
    updateConfig: (state, action: PayloadAction<Partial<OpaqueCardConfig>>) => {
      state.config = { ...state.config, ...action.payload };
    },

    setCardVariant: (state, action: PayloadAction<CardVariant>) => {
      state.cardVariant = action.payload;
    },

    setCardSize: (state, action: PayloadAction<CardSize>) => {
      state.cardSize = action.payload;
    },

    updateColorScheme: (
      state, 
      action: PayloadAction<{
        type: 'card' | 'saveIndicator' | 'passIndicator';
        scheme: Partial<SolidColorScheme>;
      }>
    ) => {
      const { type, scheme } = action.payload;
      state.colorSchemes[type] = { ...state.colorSchemes[type], ...scheme };
    },

    setEnabled: (state, action: PayloadAction<boolean>) => {
      state.isEnabled = action.payload;
    },

    updatePerformanceSettings: (
      state, 
      action: PayloadAction<Partial<OpaqueCardsSliceState['performance']>>
    ) => {
      state.performance = { ...state.performance, ...action.payload };
    },

    resetToDefaults: (state) => {
      return { ...initialState };
    },

    toggleOpacityMode: (state) => {
      state.config.disableOpacity = !state.config.disableOpacity;
      state.isEnabled = state.config.disableOpacity;
    },

    enableHighContrastMode: (state) => {
      state.config.enhanceContrast = true;
      state.performance.enableBorders = true;
      
      // Update color schemes for high contrast
      state.colorSchemes.card.border = 'border-foreground/20';
      state.colorSchemes.saveIndicator.background = 'bg-emerald-700';
      state.colorSchemes.passIndicator.background = 'bg-red-700';
    },

    disableHighContrastMode: (state) => {
      state.config.enhanceContrast = false;
      
      // Reset to default color schemes
      state.colorSchemes.card.border = 'border-border';
      state.colorSchemes.saveIndicator.background = 'bg-emerald-600';
      state.colorSchemes.passIndicator.background = 'bg-red-600';
    },
  },
});

export const {
  updateConfig,
  setCardVariant,
  setCardSize,
  updateColorScheme,
  setEnabled,
  updatePerformanceSettings,
  resetToDefaults,
  toggleOpacityMode,
  enableHighContrastMode,
  disableHighContrastMode,
} = opaqueCardsSlice.actions;

export default opaqueCardsSlice.reducer;